import { trpc } from "@/api/trpc";
import { useRoleImpersonationStore } from "@/shared/stores/role-impersonation";
import {
  type Permission,
  type UserRole,
  asUserRole,
  getPermissionsForRoles,
} from "@template/shared";

/**
 * Returns the caller's effective set of roles — the UNION of their primary
 * `role` and any supplemental `roles` from the authenticated session. When an
 * admin has opted into impersonation, ONLY the impersonated role is returned
 * (preview of what that single role sees). Pure client concept; the API never
 * trusts this — it authorises against the signed cookie.
 *
 * Empty array means "session not yet resolved" — consumers should render a
 * skeleton or hide permission-gated UI while empty.
 */
export function useEffectiveRoles(): UserRole[] {
  const { data } = trpc.auth.getSession.useQuery();
  const impersonated = useRoleImpersonationStore((s) => s.impersonatedRole);
  if (impersonated) return [impersonated];
  const primary = asUserRole(data?.profile?.role);
  if (!primary) return [];
  const rawExtra: unknown[] = Array.isArray(data?.profile?.roles) ? data.profile.roles : [];
  const extra = rawExtra.map((r) => asUserRole(r)).filter((r): r is UserRole => r !== null);
  return Array.from(new Set<UserRole>([primary, ...extra]));
}

/**
 * Back-compat single-role accessor — the primary role. Use
 * `useEffectiveRoles()` when you need the full set.
 *
 * `undefined` means "session not yet resolved".
 */
export function useEffectiveRole(): UserRole | undefined {
  return useEffectiveRoles()[0];
}

/**
 * Fast hook: "does the current viewer have this permission?" Checks the union
 * of all effective roles, so a supplemental role grants its permissions too.
 */
export function useHasPermission(perm: Permission): boolean {
  const roles = useEffectiveRoles();
  return roles.length > 0 ? getPermissionsForRoles(roles).has(perm) : false;
}
